<template>
  <fade
    :slide="slideTo"
    :speed="isMobile ? 'fast' : 'slow'"
    :is-leave-to="!isMobile"
    is-floating
  >
    <div
      v-show="model || popover"
      :id="id"
      ref="drawer"
      :popover="popover"
      :open="model"
      :class="drawerClass"
      :style="drawerStyle"
      role="dialog"
      aria-modal="true"
    >
      <div class="sf-drawer__content">
        <button
          v-if="hasCloseButton"
          type="button"
          :aria-label="$t.i19close"
          class="sf-drawer__close"
          :class="
            placement === 'end'
              ? 'sf-drawer__close--right'
              : 'sf-drawer__close--left'
          "
          @click.prevent="close"
        >
          <slot name="close">
            <span class="sf-drawer__close-icon">
              <cross-icon size="22px" />
            </span>
          </slot>
        </button>
        <slot />
      </div>
      <portal v-if="backdropTarget" :to="backdropTarget">
        <fade>
          <div v-if="model" class="sf-drawer__backdrop"></div>
        </fade>
      </portal>
    </div>
  </fade>
</template>

<script>
import Fade from '~/components/Fade.vue'
import CrossIcon from '~/components/icons/cross/Cross.vue'

export default {
  name: 'Drawer',
  components: { Fade, CrossIcon },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    placement: {
      type: String,
      default: 'start',
    },
    position: {
      type: String,
      default: 'fixed',
    },
    hasCloseButton: {
      type: Boolean,
      default: true,
    },
    anchorEl: {
      type: Object,
      default: null,
    },
    backdropTarget: {
      type: String,
      default: 'teleported-top',
    },
    canLockScroll: {
      type: Boolean,
      default: true,
    },
    maxWidth: {
      type: String,
      default: null,
    },
    id: {
      type: String,
      default: null,
    },
    popover: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      drawer: null,
      scrollbarWidth: 0,
    }
  },

  computed: {
    isMobile() {
      return this.$device.isMobile
    },
    model: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },
    slideTo() {
      switch (this.placement) {
        case 'start':
          return 'left'
        case 'end':
          return 'right'
        case 'top':
          return 'down'
        default:
          return 'up'
      }
    },
    isFixed() {
      return this.position === 'fixed'
    },
    isPlacementX() {
      return this.placement === 'start' || this.placement === 'end'
    },
    drawerClass() {
      return [
        'sf-drawer',
        `sf-drawer--${this.position}`,
        this.isPlacementX ? 'sf-drawer--x' : 'sf-drawer--y',
        this.isPlacementX && !this.maxWidth ? 'sf-drawer--max-w-sm' : '',
        this.isFixed ? 'sf-drawer--fixed' : '',
        this.isFixed && this.placement !== 'end' ? 'sf-drawer--left' : '',
        this.isFixed && this.placement === 'end' ? 'sf-drawer--right' : '',
      ]
    },
    drawerStyle() {
      const style = {}
      if (this.maxWidth) {
        style.maxWidth = `min(${this.maxWidth}, calc(100vw - ${this.scrollbarWidth}px))`
      } else if (!this.isPlacementX) {
        style.maxWidth = `calc(100vw - ${this.scrollbarWidth}px)`
      }
      return style
    },
  },

  watch: {
    model(val) {
      if (val) {
        if (this.canLockScroll) {
          this.scrollbarWidth =
            window.innerWidth - document.documentElement.clientWidth
          document.body.style.overflow = 'hidden'
          document.body.style.paddingRight = `${this.scrollbarWidth}px`
        }
        setTimeout(() => {
          document.addEventListener('click', this.outsideClickListener, {
            passive: true,
          })
          document.addEventListener('keydown', this.escClickListener, {
            passive: true,
          })
        }, 500)
      } else {
        if (this.canLockScroll) {
          document.body.style.overflow = ''
          document.body.style.paddingRight = ''
        }
        document.removeEventListener('click', this.outsideClickListener)
        document.removeEventListener('keydown', this.escClickListener)
      }
    },
  },

  mounted() {
    if (typeof window !== 'undefined' && this.popover && this.id) {
      const staticPopover = document.querySelector(`#${this.id}`)
      if (staticPopover && staticPopover.matches(':popover-open')) {
        this.model = true
      }
    }
  },

  methods: {
    close() {
      this.model = false
    },
    outsideClickListener(ev) {
      if (!this.$refs.drawer?.contains(ev.target)) {
        if (
          this.anchorEl &&
          this.anchorEl.contains &&
          this.anchorEl.contains(ev.target)
        ) {
          return
        }
        this.close()
      }
    },
    escClickListener(ev) {
      if (ev.key === 'Escape') {
        this.close()
      }
    },
  },
}
</script>

<style lang="scss">
.sf-drawer {
  z-index: 40;
  width: 93vw;
  padding: 0;
  background: #fff;
  border: none;
  position: fixed;
  top: 0;
  left: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  overflow: auto;
}
.sf-drawer--absolute {
  position: absolute;
}
.sf-drawer--fixed {
  position: fixed;
  height: 100dvh;
}
.sf-drawer--x {
  margin: 0;
}
.sf-drawer--y {
  margin-left: auto;
  margin-right: auto;
}
.sf-drawer--max-w-sm {
  max-width: 420px;
}
.sf-drawer--left {
  left: 0;
}
.sf-drawer--right {
  left: auto;
  right: 0;
}
.sf-drawer__content {
  position: relative;
  height: 100%;
}
.sf-drawer__close {
  position: absolute;
  top: 7px;
  z-index: 30;
  background: none;
  border: none;
  cursor: pointer;
}
.sf-drawer__close--right {
  right: 7px;
}
.sf-drawer__close--left {
  left: 7px;
}
.sf-drawer__close-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  background: transparent;
  transition: background-color 0.15s ease;
  width: 35px;
  height: 35px;
  border-radius: 200px;
  margin: 0;
}
.sf-drawer__close:hover .sf-drawer__close-icon {
  background: $v4_color_gray;
}
.sf-drawer__backdrop {
  position: fixed;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  z-index: 30;
  background: rgba(0, 0, 0, 0.5);
}
</style>
